import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { path } from '@bo-client/shared/constants/path';
import { EntityService } from '@bo-client/shared/dynamic-search-serverside/entity-service';
import {
  ExecutionActionsTypes,
  ActionWithMoreInfoModel,
  SecondActionTypes,
  CustomFormTypes
} from '@bo-client/activation-management/shared/models/actionWithMoreInfo.model';
import { uri } from '@bo-client/shared/constants/uri';
import { UserDto } from '@bo-client/shared/models/userDto';
import {
  FeederContractModel,
  FeederContractStatusEnum
} from '@bo-client/activation-management/feeder-contract/model/feeder-contract.model';
import { AuthenticationService } from '@bo-client/core';
import { RequestModel } from '@bo-client/shared/models/request.model';
import { Observable } from 'rxjs';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class FeederContractRequestService extends EntityService {
  showArchiveAction$: Observable<boolean>;

  constructor(
    private router: Router,
    public matDialog: MatDialog,
    private authenticationService: AuthenticationService,
    private dataShareService: DataShareService
  ) {
    super();
    this.showArchiveAction$ = this.dataShareService.tenantConfig$.pipe(
      map((config) => config.feederContract.showArchiveAction)
    );
  }

  get withoutExplicitContract() {
    return this.dataShareService.tenantConfig.feederContract.withoutExplicitContractDocument;
  }

  hasRole(user: UserDto, roles: string[]) {
    return user.roles.find((x) => roles.includes(x.name)) != undefined;
  }

  validateUIPathRequiredFields(entity: FeederContractModel): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (entity.customer?.contactType == 'Person' && !entity.customer?.lastName) {
      errors.push('Nachname ist erforderlich');
    }

    if (entity.customer?.contactType == 'Person' && !entity.customer?.firstName) {
      errors.push('Vorname ist erforderlich');
    }

    if (entity.customer?.contactType == 'Company' && !entity.customer?.company) {
      errors.push('Name der Firma ist erforderlich');
    }

    if (!entity.customer?.phone) {
      errors.push('Telefonnummer ist erforderlich');
    }

    if (!entity.customer?.mail) {
      errors.push('E-Mail ist erforderlich');
    }

    if (!entity.customer?.salutation) {
      errors.push('Anrede ist erforderlich');
    }

    if (!entity.externalGridRegistrationId) {
      errors.push('ID UDV ist erforderlich');
    }

    if (!entity.commissioningDate) {
      errors.push('Inbetriebnahmedatum ist erforderlich');
    }

    if (!entity.maStRRegistrationDate) {
      errors.push('Registrierungsdatum ist erforderlich');
    }

    if (!entity.orientation) {
      errors.push('Neigungsausrichtung ist erforderlich');
    }

    if (!entity.tilt) {
      errors.push('Neigung ist erforderlich');
    }

    if (!entity.usageType) {
      errors.push('Nutzungsart ist erforderlich');
    }

    // Validierung der Connection Object Daten (direkt im FeederContractModel)
    if (!entity.connectionObject?.street) {
      errors.push('Straße ist erforderlich');
    }

    if (!entity.connectionObject?.streetNo) {
      errors.push('Hausnummer ist erforderlich');
    }

    if (!entity.connectionObject?.zipCode) {
      errors.push('Postleitzahl ist erforderlich');
    }

    if (!entity.connectionObject?.city) {
      errors.push('Ort ist erforderlich');
    }

    if (!entity.feedInManagementInfo) {
      errors.push('Einspeisermanagement ist erforderlich');
    }

    // Validierung der Producer Daten
    if (!entity.producers || entity.producers.length === 0) {
      errors.push('Wechselrichterleistung ist erforderlich');
    } else {
      const totalPower = entity.producers.reduce(
        (sum, producer) => sum + (producer.acActivePowerMax || 0),
        0
      );
      if (totalPower === 0) {
        errors.push('Wechselrichterleistung muss größer als 0 sein');
      }
    }

    // Validierung der PV Module
    if (!entity.pvModules || entity.pvModules.length === 0) {
      errors.push('PV Module sind erforderlich');
    } else {
      const totalCount = entity.pvModules.map((x) => x.count).reduce((sum, current) => sum + current, 0);
      if (totalCount === 0) {
        errors.push('Anzahl Module muss größer als 0 sein');
      }

      const totalPower = entity.pvModules.reduce((sum, module) => sum + (module.modulePowerWatt || 0), 0);
      if (totalPower === 0) {
        errors.push('Leistung Brutto muss größer als 0 sein');
      }
    }

    // Validierung der Speicher
    if (entity.hasStorage) {
      if (!entity.storages || entity.storages.length === 0) {
        errors.push('Speicherdaten sind erforderlich wenn Speicher vorhanden');
      } else {
        const storage = entity.storages[0];
        if (!storage.couplingType) {
          errors.push('Art der Kopplung Speicher ist erforderlich');
        }
      }
    }

    // Validierung des Metering Concepts
    if (!entity.meteringConcept?.isFullFeedIn && !entity.meteringConcept?.isFullFeedOut) {
      errors.push('Art der Einspeisung (T, V) ist erforderlich');
    }

    // Validierung der Kompensationskategorien
    if (!entity.compensationCategories || entity.compensationCategories.length === 0) {
      errors.push('Preisschlüssel ist erforderlich');
    }

    if (!entity.maStRSELNumber) {
      errors.push('Mastr-SEL-Nummer ist erforderlich');
    }

    if (!entity.maStREEGNumber) {
      errors.push('Mastr-EEG-Nummer ist erforderlich');
    }

    if (!entity.maStRNumberStorage && entity.hasStorage) {
      errors.push('Mastr-Nummer Speicher ist erforderlich');
    }

    if (!entity.maStREEGNumberStorage && entity.hasStorage) {
      errors.push('Mastr-EEG-Nummer Speicher ist erforderlich');
    }

    if (!entity.maStRSSENumberStorage && entity.hasStorage) {
      errors.push('Mastr-SSE-Nummer Speicher ist erforderlich');
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  actions: ActionWithMoreInfoModel[] = [
    {
      action: 'save',
      actionName: 'Speichern',
      actionUrl: uri.cmFeederContract,
      successMessage: 'Erfolgreich gespeichert',
      failureMessage: 'Fehler beim Speichern',
      subject: '',
      validateForm: false,
      visibleCondition: () => {
        return this.authenticationService.isLoggedIn;
      },
      execute: ExecutionActionsTypes.save
    },
    {
      action: 'completeTechnicalReview',
      actionName: 'Fachliche Prüfung abschließen',
      actionUrl: uri.cmFeederContractTechnicallyReviewed,
      successMessage: 'Fachliche Prüfung erfolgreich abgeschlossen',
      failureMessage: 'Fehler beim Abschließen der fachlichen Prüfung',
      subject: '',
      validateForm: false,
      visibleCondition: (entity: FeederContractModel, state, user) => {
        return (
          this.dataShareService.tenantConfig.feederContract.showAdditionalCheckMastrDataAction &&
          entity.state === FeederContractStatusEnum.Created &&
          this.authenticationService.isLoggedIn &&
          this.hasRole(user, ['ROLE_EDITOR_BASE'])
        );
      },
      execute: ExecutionActionsTypes.checkIfErrors
    },
    {
      action: 'sendOfferToCustomer',
      actionName: 'Vertragsangebot per E-Mail senden',
      actionUrl: uri.cmFeederContractSendOfferToCustomer,
      successMessage: 'Vertragsangebot per E-Mail erfolgreich versandt',
      failureMessage: 'Fehler beim Versenden des Angebots per E-Mail',
      subject: 'Ihr Vertrag zur Einspeiseanlage',
      validateForm: false,
      visibleCondition: (entity: FeederContractModel) => {
        return (
          ((!this.dataShareService.tenantConfig.feederContract.showAdditionalCheckMastrDataAction &&
            entity.state === FeederContractStatusEnum.Created) ||
            entity.state === FeederContractStatusEnum.TechnicallyReviewed) &&
          this.authenticationService.isLoggedIn &&
          !this.withoutExplicitContract
        );
      },
      execute: ExecutionActionsTypes.confirmActionWithComment
    },
    {
      action: 'reSendOfferToCustomer',
      actionName: 'Vertragsangebot erneut versenden',
      actionUrl: uri.cmFeederContractReSendOfferToCustomer,
      successMessage: 'Vertragsangebot per E-Mail erfolgreich versandt',
      failureMessage: 'Fehler beim Versenden des Angebots per E-Mail',
      subject: 'Ihr Vertrag zur Einspeiseanlage',
      validateForm: false,
      visibleCondition: (entity: FeederContractModel) => {
        return (
          entity.state === FeederContractStatusEnum.ConfirmationRequestedByMail &&
          this.authenticationService.isLoggedIn &&
          !this.withoutExplicitContract
        );
      },
      execute: ExecutionActionsTypes.confirmActionWithComment
    },
    {
      action: 'reSendOfferToCustomer_withoutContract',
      actionName: 'Stammdaten erneut per E-Mail anfragen',
      actionUrl: uri.cmFeederContractReSendOfferToCustomer,
      successMessage: 'Stammdatenerfassung per E-Mail erfolgreich versandt',
      failureMessage: 'Fehler beim Versenden',
      subject: 'Wir benötigen noch folgende Informationen von Ihnen',
      validateForm: false,
      visibleCondition: (entity: FeederContractModel) => {
        return (
          entity.state === FeederContractStatusEnum.ConfirmationRequestedByMail &&
          this.authenticationService.isLoggedIn &&
          this.withoutExplicitContract
        );
      },
      execute: ExecutionActionsTypes.confirmActionWithComment
    },
    {
      action: 'sendOfferToCustomer_withoutContract',
      actionName: 'Stammdaten per E-Mail anfragen',
      actionUrl: uri.cmFeederContractSendOfferToCustomer,
      successMessage: 'Stammdatenerfassung per E-Mail erfolgreich versandt',
      failureMessage: 'Fehler beim Versenden',
      subject: 'Wir benötigen noch folgende Informationen von Ihnen',
      validateForm: false,
      visibleCondition: (entity: FeederContractModel) => {
        return (
          (entity.state === FeederContractStatusEnum.Created ||
            entity.state === FeederContractStatusEnum.TechnicallyReviewed) &&
          this.authenticationService.isLoggedIn &&
          this.withoutExplicitContract
        );
      },
      execute: ExecutionActionsTypes.confirmActionWithComment
    },
    {
      action: 'acceptOffer',
      actionName: 'Annahme Vertragsangebot',
      actionUrl: uri.cmFeederContractExternalAcceptedContract,
      successMessage: 'Daten erfolgreich versandt',
      failureMessage: 'Fehler beim Senden des Vertragsangebot',
      subject: '',
      validateForm: true,
      visibleCondition: () => {
        return !this.authenticationService.isLoggedIn;
      },
      execute: ExecutionActionsTypes.executeAction
    },
    {
      action: 'acceptOfferWithoutContract',
      actionName: 'Annahme Vertragsangebot',
      actionUrl: uri.cmFeederContractExternalAcceptedWithoutContract,
      successMessage: 'Daten erfolgreich versandt',
      failureMessage: 'Fehler beim Senden des Vertragsangebot',
      subject: '',
      validateForm: true,
      visibleCondition: () => {
        return !this.authenticationService.isLoggedIn;
      },
      execute: ExecutionActionsTypes.executeAction
    },
    {
      action: 'requestFeedback',
      actionName: 'Rückfragen',
      actionUrl: uri.cmFeederContractRequestFeedback,
      successMessage: 'Rückfrage versandt',
      failureMessage: 'Fehler beim Versenden der Rückfrage',
      subject: 'Rückfrage zu Ihrer Anlage',
      validateForm: false,
      visibleCondition: (entity, state, user) => {
        return (
          entity.state === FeederContractStatusEnum.Confirmed && this.hasRole(user, ['ROLE_EDITOR_BASE'])
        );
      },
      execute: ExecutionActionsTypes.confirmActionWithComment
    },
    {
      action: 'changeStatus',
      actionName: 'Status ändern',
      actionUrl: uri.cmFCChangeStatus,
      secondAction: SecondActionTypes.saveModelAndActionPlusMail,
      successMessage: 'Der Status wurde geändert',
      failureMessage: 'Fehler bei Statusänderung der Netzanmeldung.',
      recipients: 'Installateur',
      subject: 'Status ändern',
      validateForm: false,
      customForm: CustomFormTypes.FcChangeStatus,
      actionType: 'secondary',
      tooltip: 'Keyuser Funktion um den Vorgang in einen gewünschten Status zu versetzen.',
      visibleCondition: (entity, state, user) => {
        return this.hasRole(user, ['ROLE_ADMIN', 'ROLE_KEYUSER_FEEDERCONTRACTS']);
      },
      execute: ExecutionActionsTypes.customFormWithCustomActionModal
    },
    {
      action: 'approve',
      actionName: 'Abschließen',
      actionUrl: uri.cmFeederContractApproved,
      successMessage: 'Erfolgreich abgeschlossen',
      failureMessage: 'Fehler beim Abschließen',
      subject: 'Ihr Einspeiservertrag',
      validateForm: false,
      visibleCondition: (entity: FeederContractModel, state, user) => {
        return (
          entity.state === FeederContractStatusEnum.Confirmed &&
          this.hasRole(user, ['ROLE_EDITOR_BASE']) &&
          !this.withoutExplicitContract
        );
      },
      execute: ExecutionActionsTypes.confirmActionWithComment
    },
    {
      action: 'approve',
      actionName: 'Abschließen',
      actionUrl: uri.cmFeederContractApproved,
      successMessage: 'Erfolgreich abgeschlossen',
      failureMessage: 'Fehler beim Abschließen',
      subject: 'Bestätigung zur Anmeldung Ihrer Anlage',
      validateForm: false,
      visibleCondition: (entity: FeederContractModel, state, user) => {
        return (
          entity.state === FeederContractStatusEnum.Confirmed &&
          this.hasRole(user, ['ROLE_EDITOR_BASE']) &&
          this.withoutExplicitContract
        );
      },
      execute: ExecutionActionsTypes.confirmActionWithComment
    },
    {
      action: 'checkIfErrors',
      actionName: 'Fehler prüfen',
      actionUrl: null,
      successMessage: '',
      failureMessage: '',
      subject: '',
      validateForm: true,
      visibleCondition: () => {
        return false;
      },
      execute: ExecutionActionsTypes.checkIfErrors
    },
    {
      action: 'createDocumentV2',
      actionName: 'Dokument erzeugen',
      actionUrl: '',
      successMessage: 'Dokument erfolgreich erzeugt',
      failureMessage: 'Fehler beim Erzeugen des Dokumentes',
      subject: '',
      validateForm: false,
      visibleCondition: (entity: FeederContractModel) => {
        return (
          this.authenticationService.isLoggedIn &&
          !(entity.state === FeederContractStatusEnum.ToBeCreated) &&
          (entity.state === FeederContractStatusEnum.TechnicallyReviewed ||
            entity.state !== FeederContractStatusEnum.Created)
        );
      },
      execute: ExecutionActionsTypes.createDocumentV2
    },
    {
      action: 'archiveContract',
      actionName: 'Archivieren',
      actionUrl: uri.cmFeederContract + uri.archive,
      successMessage: 'Vertrag erfolgreich archiviert',
      failureMessage: 'Fehler beim Archivieren',
      subject: 'Ihr Einspeiservertrag',
      validateForm: false,
      visibleCondition: () => {
        // Todo: Observable hier einfügen?
        // this.showArchiveAction$
        return this.dataShareService.tenantConfig.feederContract.showArchiveAction || false;
      },
      execute: ExecutionActionsTypes.confirmAction
    }
  ].map(
    (action) =>
      ({
        ...action,
        visibleCondition: (entity: FeederContractModel, state, user) => {
          if (
            !this.hasRole(user, ['ROLE_KEYUSER_FEEDERCONTRACTS']) &&
            !this.hasRole(user, ['ROLE_EDITOR_FEEDERCONTRACTS']) &&
            !this.hasRole(user, ['ROLE_SUPERADMIN'])
          ) {
            return false;
          }
          return action.visibleCondition(entity, state, user);
        }
      } as ActionWithMoreInfoModel)
  );

  navigateToCreatePage() {
    this.router.navigate([path.cmPrefix + '/' + path.cmFeederContract + path.create], { replaceUrl: false });
  }

  navigateToCreateFormGridRegPage(idGrReg: number) {
    this.router.navigate(
      [path.cmPrefix + '/' + path.cmFeederContract + '/' + path.createDraft + '/' + idGrReg],
      {
        replaceUrl: false
      }
    );
  }

  navigateToEditPage(id: string | number) {
    this.router.navigate([path.cmPrefix + '/' + path.cmFeederContract + path.edit + '/' + id], {
      replaceUrl: false
    });
  }

  loadData(
    id: string,
    master: string,
    number: number,
    number2: number,
    filterModel: { [p: string]: any }
  ): Observable<{
    content: RequestModel[];
    totalElements: number;
  }> {
    return undefined;
  }
}
